
import VueFilters from "@/vue-filters";
import { Component, Prop, Vue } from "vue-property-decorator";
import { BvTableFieldArray } from "bootstrap-vue";
import { i18n } from "@/i18n";
import {
  ICountryConfig,
  IRequestedAmount,
  IRequestHistorialsService,
  ISolCustomerCancellationReason,
  ISolCustomerPack,
  IUserClientSubclientServicepointData,
  resolveSolCustomerOrderDescriptionKey,
  resolveSolCustomerOrderType,
} from "@/entities";
import DetailOrderModal from "@/components/orders/DetailOrderModal.vue";
import RatingOrderModal from "@/components/orders/RatingOrderModal.vue";
import { mapGetters } from "vuex";
import IconTrash from "@/components/common/icon/IconTrash.vue";
import CancelOrderModal from "@/components/orders/CancelOrderModal.vue";
import { Getter } from "vuex-class";

@Component({
  name: "request-historial-table",
  mixins: [VueFilters],
  computed: {
    ...mapGetters(["getLocale"]),
  },
  components: { IconTrash, DetailOrderModal, RatingOrderModal, CancelOrderModal },
})
export default class RequestHistorialTable extends Vue {
  @Prop({ required: false, default: "" }) title!: string;
  @Prop({ required: false, default: null }) services!: IRequestHistorialsService[];
  @Prop({ required: false }) entityId!: string;
  @Prop({ required: true }) cancellationReasons!: ISolCustomerCancellationReason[];
  @Prop({ required: false, default: true }) collapsed!: boolean;
  @Prop({ required: false, default: false }) agilePickup!: boolean;
  @Prop({ required: true }) packs!: ISolCustomerPack[];
  @Prop({ required: false, default: false }) isOrdersTable!: boolean;
  @Prop({ required: true }) enabledCancelOrders!: boolean;
  @Prop({ required: true }) enabledPlanningDate!: boolean;
  @Prop({ required: false, default: null })
  servicepointSelected!: IUserClientSubclientServicepointData | null;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig;

  serviceSelected: string | null = null;
  serviceType: number | null = null;

  get fields(): BvTableFieldArray {
    if (this.isOrdersTable) {
      return [
        {
          key: "actions-left",
          label: "",
          sortable: false,
          thClass: "table-header pl-4",
          tdClass: "table-cell-first align-middle position-relative",
        },
        {
          key: "serviceType",
          label: i18n.t("column.orderType") as string,
          sortable: false,
          thClass: "table-header",
          tdClass: "table-cell-middle align-middle",
          formatter: (value: number, _key: string, item: IRequestHistorialsService): string => {
            return this.resolveServiceTypeDescription(value, item, false);
          },
        },
        {
          key: "createdAt",
          label: i18n.t("column.creation_date") as string,
          sortable: false,
          thClass: "table-header",
          tdClass: "table-cell-middle align-middle",
        },
        {
          key: "approvedAt",
          label: i18n.t("column.update_date") as string,
          sortable: false,
          thClass: "table-header",
          tdClass: "table-cell-middle align-middle",
        },
        {
          key: "serviceStatusDescription",
          label: i18n.t("column.status") as string,
          sortable: false,
          thClass: "table-header",
          tdClass: "table-cell-middle align-middle",
          formatter: (value: string): string => {
            return i18n.te(value) ? i18n.t(value) : value;
          },
        },
        {
          key: "requestedAmounts",
          label: i18n.t("column.amount") as string,
          sortable: false,
          thClass: "table-header text-right",
          tdClass: "table-cell-middle text-right align-middle",
        },
        {
          key: "actions-right",
          label: "",
          sortable: false,
          thClass: "table-header",
          tdClass: "table-cell-last align-middle",
        },
      ];
    }
    return [
      {
        key: "actions-left",
        label: "",
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first align-middle position-relative",
      },
      {
        key: "serviceType",
        label: i18n.t("column.orderType") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
        formatter: (value: number, _key: string, item: IRequestHistorialsService): string => {
          return this.resolveServiceTypeDescription(value, item, true);
        },
      },
      {
        key: "serviceCode",
        label: i18n.t("column.id") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "requestInitialTime",
        label: i18n.t("column.requested") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      ...(this.enabledPlanningDate
        ? [
            {
              key: "plannedInitialTime",
              label: i18n.t("column.planned") as string,
              sortable: false,
              thClass: "table-header",
              tdClass: "table-cell-middle align-middle",
            },
          ]
        : []),
      {
        key: "realInitialTime",
        label: i18n.t("column.completed") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "serviceStatusDescription",
        label: i18n.t("column.status") as string,
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-middle align-middle",
      },
      {
        key: "requestedAmounts",
        label: i18n.t("column.amount") as string,
        sortable: false,
        thClass: "table-header text-right",
        tdClass: "table-cell-middle text-right align-middle",
      },
      {
        key: "actions-right",
        label: "",
        sortable: false,
        thClass: "table-header",
        tdClass: "table-cell-last align-middle",
      },
    ];
  }

  resolveServiceTypeDescription(
    serviceTypeValue: number,
    item: IRequestHistorialsService,
    useProductDescription: boolean
  ): string {
    if (useProductDescription && item.productCode && item.productDescription) {
      return i18n.t(item.productDescription).toString();
    }

    const orderType = resolveSolCustomerOrderType(item.productCode, serviceTypeValue);
    const descriptionKey = resolveSolCustomerOrderDescriptionKey(orderType, item.isChange);

    return descriptionKey ? i18n.t(descriptionKey).toString() : serviceTypeValue.toString();
  }

  sumRequestedAmounts(requestedAmounts: IRequestedAmount[] | undefined): IRequestedAmount[] {
    const amountsByCurrency: IRequestedAmount[] = [];
    requestedAmounts?.forEach((value) => {
      const amountByCurrency = amountsByCurrency.find(
        (requestedAmount) => requestedAmount.currencyIsoCode === value.currencyIsoCode
      );

      if (amountByCurrency) {
        amountByCurrency.totalAmount =
          (amountByCurrency.totalAmount || 0) + (value.totalAmount || 0);
      } else {
        amountsByCurrency.push(Object.assign({}, value));
      }
    });
    return amountsByCurrency;
  }

  openDetailModal(row: IRequestHistorialsService): void {
    this.serviceSelected = row.serviceId || null;
    this.serviceType = row.serviceType || null;
    const detailOrderModal = this.$refs.detailOrderModal as DetailOrderModal;
    detailOrderModal.$emit("showDetailOrderModal", row);
  }

  openRatingModal(row: IRequestHistorialsService): void {
    this.serviceSelected = row.serviceId || null;
    this.serviceType = row.serviceType || null;
    const ratingOrderModal = this.$refs.ratingOrderModal as RatingOrderModal;
    ratingOrderModal.$emit("showRatingOrderModal", row);
  }

  openCancelOrderModal(row: IRequestHistorialsService): void {
    const cancelOrderModal = this.$refs.cancelOrderModal as CancelOrderModal;
    cancelOrderModal.$emit("showCancelOrderModal", row);
  }

  isCancellable(row: IRequestHistorialsService): boolean {
    const remesaStatusIsValid = row.consignmentStatusCode
      ? this.remesaStatusIsValid(row.consignmentStatusCode)
      : true;

    if (
      this.enabledCancelOrders &&
      row.serviceId &&
      row.serviceStatusCode !== "CANCELLED" &&
      row.serviceStatusCode !== "DONE" &&
      row.serviceStatusCode !== "ONWAY" &&
      this.daysDiff(row.requestInitialTime) >= 1 &&
      remesaStatusIsValid
    ) {
      switch (row.serviceType) {
        case 0:
          return this.$ability.can("manage", "Pickup");
        case 1:
          if (row.isChange) {
            return this.$ability.can("manage", "DeliveryExchange");
          } else {
            return this.$ability.can("manage", "Delivery");
          }
        case 3:
          return this.$ability.can("manage", "AssistanceService");
        case 4:
          return this.$ability.can("manage", "SupplyMaterialService");
      }
    }

    return false;
  }

  remesaStatusIsValid(consigmentStatusValue: string): boolean {
    const consignmentStatusCodeStringOrNumber = consigmentStatusValue.toString();
    const validConsignmentStatusCodes = ["2"];
    return validConsignmentStatusCodes.includes(consignmentStatusCodeStringOrNumber);
  }

  daysDiff(dateString?: string): number {
    if (dateString) {
      const date = new Date(dateString);

      const differenceInTime = date.getTime() - Date.now();

      return differenceInTime / (1000 * 3600 * 24);
    }
    return 1;
  }
}
